import { Filter, Plus, Trash2 } from 'lucide-react';
import { EmptyState } from '@vnetwork/ui/data';
import {
  Input,
  Switch,
  Button,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { LbListener, LbHealthCheck, LbAclRule, LbRuleServer } from '@/api/opennebula';

const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--vn-space-3)' };
const toggleRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)' };
const num = (v?: number) => (v == null ? '' : String(v));
const toNum = (s: string) => (s === '' ? undefined : Number(s));

/** Advanced HAProxy listener settings (timeouts + L7 options for HTTP/HTTPS). */
export function ListenerAdvancedFields({
  listener,
  onChange,
  isHttp,
  isHttps,
}: {
  listener: LbListener;
  onChange: (patch: Partial<LbListener>) => void;
  isHttp: boolean;
  isHttps: boolean;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--vn-space-3)' }}>
        <Field label="Connect timeout (s)">{(p) => <Input {...p} type="number" value={num(listener.connect_timeout)} onChange={(e) => onChange({ connect_timeout: toNum(e.target.value) })} />}</Field>
        <Field label="Client timeout (s)">{(p) => <Input {...p} type="number" value={num(listener.client_timeout)} onChange={(e) => onChange({ client_timeout: toNum(e.target.value) })} />}</Field>
        <Field label="Server timeout (s)">{(p) => <Input {...p} type="number" value={num(listener.server_timeout)} onChange={(e) => onChange({ server_timeout: toNum(e.target.value) })} />}</Field>
      </div>

      {isHttps && (
        <div style={grid2}>
          <Field label="SSL policy">
            {() => (
              <Select value={listener.https_ssl_policy ?? ''} onValueChange={(v) => onChange({ https_ssl_policy: v })}>
                <SelectTrigger><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent>{['compatible', 'intermediate', 'modern'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Minimum TLS">
            {() => (
              <Select value={listener.https_min_tls ?? ''} onValueChange={(v) => onChange({ https_min_tls: v })}>
                <SelectTrigger><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent>{['1.0', '1.1', '1.2', '1.3'].map((p) => <SelectItem key={p} value={p}>TLS {p}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </Field>
        </div>
      )}

      {isHttp && (
        <>
          <Field label="ALPN protocols">{(p) => <Input {...p} value={listener.alpn_protocols ?? ''} onChange={(e) => onChange({ alpn_protocols: e.target.value })} placeholder="h2,http/1.1" />}</Field>
          <Field label="Custom headers">{(p) => <Input {...p} value={listener.custom_headers ?? ''} onChange={(e) => onChange({ custom_headers: e.target.value })} placeholder="X-Env: prod" />}</Field>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-4)' }}>
            <label style={toggleRow}><Switch checked={!!listener.xff_enabled} onCheckedChange={(v) => onChange({ xff_enabled: v })} /> X-Forwarded-For</label>
            <label style={toggleRow}><Switch checked={!!listener.xfp_enabled} onCheckedChange={(v) => onChange({ xfp_enabled: v })} /> X-Forwarded-Proto</label>
            <label style={toggleRow}><Switch checked={!!listener.xfport_enabled} onCheckedChange={(v) => onChange({ xfport_enabled: v })} /> X-Forwarded-Port</label>
          </div>
          <div style={grid2}>
            <label style={toggleRow}><Switch checked={!!listener.cors_enabled} onCheckedChange={(v) => onChange({ cors_enabled: v })} /> Enable CORS</label>
            {listener.cors_enabled && <Input value={listener.cors_origins ?? ''} onChange={(e) => onChange({ cors_origins: e.target.value })} placeholder="https://app.example.com" />}
          </div>
          <div style={grid2}>
            <label style={toggleRow}><Switch checked={!!listener.sticky_enabled} onCheckedChange={(v) => onChange({ sticky_enabled: v })} /> Sticky sessions</label>
            {listener.sticky_enabled && <Input value={listener.sticky_cookie ?? ''} onChange={(e) => onChange({ sticky_cookie: e.target.value })} placeholder="SRVID" />}
          </div>
        </>
      )}
    </div>
  );
}

/** Active health-check probe editor. */
export function HealthCheckEditor({ value, onChange, isHttp }: { value?: LbHealthCheck; onChange: (hc: LbHealthCheck) => void; isHttp: boolean }) {
  const hc = value ?? { enabled: false, type: isHttp ? 'http' : 'tcp' };
  const set = (patch: Partial<LbHealthCheck>) => onChange({ ...hc, ...patch });
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <label style={toggleRow}><Switch checked={!!hc.enabled} onCheckedChange={(v) => set({ enabled: v })} /> Enable health check</label>
      {hc.enabled && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 'var(--vn-space-3)' }}>
            <Field label="Type">
              {() => (
                <Select value={hc.type} onValueChange={(v) => set({ type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['tcp', 'http', 'https'].map((t) => <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Interval (s)">{(p) => <Input {...p} type="number" value={num(hc.interval)} onChange={(e) => set({ interval: toNum(e.target.value) })} placeholder="5" />}</Field>
            <Field label="Timeout (s)">{(p) => <Input {...p} type="number" value={num(hc.timeout)} onChange={(e) => set({ timeout: toNum(e.target.value) })} placeholder="2" />}</Field>
            <Field label="Retries">{(p) => <Input {...p} type="number" value={num(hc.retries)} onChange={(e) => set({ retries: toNum(e.target.value) })} placeholder="3" />}</Field>
          </div>
          {/^https?$/.test(hc.type) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)', gap: 'var(--vn-space-3)' }}>
              <Field label="HTTP path">{(p) => <Input {...p} value={hc.http_path ?? ''} onChange={(e) => set({ http_path: e.target.value })} placeholder="/health" />}</Field>
              <Field label="Status">{(p) => <Input {...p} type="number" value={num(hc.http_expected_status)} onChange={(e) => set({ http_expected_status: toNum(e.target.value) })} placeholder="200" />}</Field>
              <Field label="Host">{(p) => <Input {...p} value={hc.http_host ?? ''} onChange={(e) => set({ http_host: e.target.value })} placeholder="api.example.com" />}</Field>
              <Field label="HTTP ver">
                {() => (
                  <Select value={hc.http_version ?? '1.1'} onValueChange={(v) => set({ http_version: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{['1.0', '1.1'].map((v) => <SelectItem key={v} value={v}>HTTP/{v}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          )}
          {/^https?$/.test(hc.type) && (
            <p style={{ margin: 0, fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-warning-text, var(--vn-fg-muted))' }}>
              Requires HAProxy image v4.X+ template update. 1.0 = connection-close; 1.1 = keep-alive.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** L7 ACL routing-rule builder (HTTP/HTTPS). */
export function AclRuleEditor({ value, onChange }: { value: LbAclRule[]; onChange: (rules: LbAclRule[]) => void }) {
  const setRule = (i: number, patch: Partial<LbAclRule>) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const addRule = () => onChange([...value, { conditions: [''], action: '', combinator: 'and', servers: [] }]);
  const del = (i: number) => onChange(value.filter((_, j) => j !== i));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>Route requests matching conditions to specific backends.</span>
        <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} onClick={addRule}>Add rule</Button>
      </div>
      <div style={{ padding: 'var(--vn-space-2)', borderRadius: 'var(--vn-radius-md)', background: 'var(--vn-warning-subtle)', border: '1px solid var(--vn-warning-border)' }}>
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-warning-text, var(--vn-fg-muted))' }}>
          Extended match types (<code>method</code>, <code>query</code>), operators (<code>regex</code>, <code>not_eq</code>) and the
          combinator require <strong>HAProxy image v4.X+</strong> with a template update. Conditions are saved but only legacy
          <code> host/path/header</code> with <code>eq/contains/starts_with</code> are actively enforced until the template is updated.
        </span>
      </div>
      {value.length === 0 ? (
        <EmptyState icon={<Filter size={20} />} title="No ACL rules yet" description="Add a routing condition to control traffic for this listener." />
      ) : value.map((r, i) => (
        <div key={i} style={{ border: '1px solid var(--vn-border-default)', borderRadius: 'var(--vn-radius-md)', padding: 'var(--vn-space-3)', display: 'grid', gap: 'var(--vn-space-2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontSize: 'var(--vn-font-size-sm)' }}>Rule {i + 1}</strong>
            <IconButton variant="ghost" size="sm" aria-label="Remove rule" onClick={() => del(i)}><Trash2 size={14} /></IconButton>
          </div>
          <Field label="Conditions ([!]field:operator:value, comma-separated)">
            {(p) => <Input {...p} value={r.conditions.join(', ')} onChange={(e) => setRule(i, { conditions: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="path_beg:/api, hdr(host):-i:api.example.com" />}
          </Field>
          <div style={grid2}>
            <Field label="Action">
              {() => (
                <Select value={r.action || 'backend'} onValueChange={(v) => setRule(i, { action: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['backend', 'redirect', 'deny'].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Combinator">
              {() => (
                <Select value={r.combinator ?? 'and'} onValueChange={(v) => setRule(i, { combinator: v as 'and' | 'or' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="and">AND</SelectItem>
                    <SelectItem value="or" disabled>OR (coming soon)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <AclServers value={r.servers ?? []} onChange={(servers) => setRule(i, { servers })} />
        </div>
      ))}
    </div>
  );
}

function AclServers({ value, onChange }: { value: LbRuleServer[]; onChange: (s: LbRuleServer[]) => void }) {
  const set = (i: number, patch: Partial<LbRuleServer>) => onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--vn-font-size-sm)', fontWeight: 600 }}>Backends</span>
        <Button size="sm" variant="ghost" startIcon={<Plus size={13} />} onClick={() => onChange([...value, { host: '', port: '' }])}>Add backend</Button>
      </div>
      {value.map((s, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) auto', gap: 'var(--vn-space-2)' }}>
          <Input style={{ minWidth: 0 }} value={s.host} onChange={(e) => set(i, { host: e.target.value })} placeholder="10.0.0.10" />
          <Input style={{ minWidth: 0 }} value={s.port} onChange={(e) => set(i, { port: e.target.value })} placeholder="Port" />
          <IconButton variant="ghost" size="sm" aria-label="Remove backend" onClick={() => onChange(value.filter((_, j) => j !== i))}><Trash2 size={13} /></IconButton>
        </div>
      ))}
    </div>
  );
}
